import { syncRule, childId, validity } from '../common/decoration/decorators';
import type {
    v4,
    ViewTypes,
    Target,
    Manifest,
    Application,
    Dependencies,
    CloudDevAdaptationStatus
} from '@sap/ux-specification-types';
import { UIAnnotationTerms } from '@sap-ux/vocabularies-types/vocabularies/UI';
import { QUICKVARPATH } from '@sap/ux-specification-types';
import { controllerExtensions } from './sync-rules/controllerExtensions';
import type { EmptyOption } from '@sap/ux-specification-types/src/v4/controls/Table';
import type { TableCreationModeType } from '@sap/ux-specification-types/dist/v4/controls/ObjectPageTable';
import type { PopinLayout } from 'sap/m/library';

/**
 * Returns the sync path for a given page name array.
 *
 * @param {string[]} pageName - Array containing the page name(s) for which to generate the manifest path.
 * @returns {string} The sync path string for the specified page.
 */
export const getPathForPage = (pageName: string[]): string => {
    return `sap.ui5/routing/targets/${pageName[0]}`;
};

export const getSettingsPathForPage = (pageName: string[]): string => {
    return `${getPathForPage(pageName)}/options/settings`;
};

export const getSettingsPathForLRViewsPaths = (pageName: string[]): string => {
    return getSettingsPathForPage(pageName) + `/views/paths`;
};

export const getSettingsPathForHeaderOP = (pageName: string[]): string => {
    return getSettingsPathForPage(pageName) + `/content/header`;
};

export const getSettingsPathForFooterOP = (pageName: string[]): string => {
    return getSettingsPathForPage(pageName) + `/content/footer`;
};

export const getControlConfigurationPath = (pageName: string[]): string => {
    return getSettingsPathForPage(pageName) + '/controlConfiguration';
};

export const getHeaderFacetSettingPath = (pageName: string[], _manifest: Manifest, sectionId: string): string => {
    return getControlConfigurationPath(pageName) + `/@${UIAnnotationTerms.HeaderFacets}/facets/${sectionId}`;
};

export const getSelectionFieldsPath = (pageName: string[]): string => {
    return getControlConfigurationPath(pageName) + `/@${UIAnnotationTerms.SelectionFields}`;
};

/**
 * Common function that calculates the path in manifest for custom filter field.
 *
 * @param pageName - page name in manifest
 * @param _manifest - manifest (not used)
 * @param _sectionId - section id (not used)
 * @param filterFieldId - Custom filter field id
 * @returns - path as string
 */
export const getCustomFilterFieldFieldPath = (
    pageName: string[],
    _manifest: Manifest,
    _sectionId: string,
    filterFieldId
): string => {
    return (
        getControlConfigurationPath(pageName) + `/@${UIAnnotationTerms.SelectionFields}/filterFields/${filterFieldId}`
    );
};

export const getFilterFieldPath = (
    pageName: string[],
    _manifest: Manifest,
    _targetAnnotationEncoded: string,
    fieldId: string
): string => {
    return getSelectionFieldsPath(pageName) + `/filterFields/${fieldId}`;
};

/**
 * Common function that calculates the path in manifest for table settings of a list report or ALP table view.
 *
 * @param pageName - page name in manifest
 * @param _manifest - manifest (not used)
 * @param _targetAnnotationEncoded - encoded target annotation (not used)
 * @param _viewKey - view key (not used)
 * @param targetAnnotation - target annotation, i.e. the line item (with or without qualifier)
 * @returns - path as string
 */
export const getTableSettingsPathForView = (
    pageName: string[],
    _manifest: Manifest,
    _targetAnnotationEncoded: string,
    _viewKey: string,
    targetAnnotation: string
): string => {
    const annotationEncoded = targetAnnotation?.replace(/\//gi, '??');
    return annotationEncoded
        ? getControlConfigurationPath(pageName) + `/${annotationEncoded}/tableSettings`
        : undefined;
};

/**
 * Determines if the view is newly created based on its identifier structure and annotation state.
 *
 * This function checks if the concatenation of original identifiers starts with the specific prefix
 * 'table/views' and ensures the target annotation is undefined, indicating the view's newly created status.
 *
 * @param {string[]} originalIds - An array of strings representing original identifiers related to the view.
 * @param {string} targetAnnotation - A string representing the current annotation state of the view.
 * @returns {boolean} Returns true if the view is determined to be newly created; otherwise, false.
 */
const isNewlyCreatedView = (originalIds?: string[], targetAnnotation?: string) => {
    return (originalIds ?? []).join('/').startsWith('table/views') && targetAnnotation === undefined;
};

/**
 * Generates the path for table settings within a list report (LR) configuration.
 *
 * This function determines the appropriate path for accessing the table settings
 * based on whether a target annotation is provided or other fallback logic for
 * primary and secondary views.
 *
 * @param pageName The array of strings representing the current page's hierarchy in the manifest.
 * @param manifest The manifest object containing the application configuration.
 * @param targetAnnotationEncoded The encoded string of the target annotation for the table settings.
 * @param columnKey The key of the column for which table settings are to be determined.
 * @param targetAnnotation The decoded target annotation string, if available.
 * @param configObject A record object containing additional configuration details.
 * @param originalIds An array of original table IDs used for validation in creating a view.
 * @returns The path to the table settings as a string. Returns an empty string when the
 *          table is part of a secondary view with missing settings or fallback to a primary view default path.
 */
export const getTableSettingsPathForLR = (
    pageName: string[],
    manifest: Manifest,
    targetAnnotationEncoded: string,
    columnKey: string,
    targetAnnotation: string,
    configObject: Record<string, unknown>,
    originalIds: string[]
): string => {
    if (targetAnnotation) {
        return getTableSettingsPathForView(pageName, manifest, targetAnnotationEncoded, columnKey, targetAnnotation);
    } else {
        const isConfigObjectEmpty = Object.keys(configObject ?? {}).length === 0;
        // If the view is newly created, and the table settings are not set, this is the secondary view. The path should be empty
        if (isConfigObjectEmpty && isNewlyCreatedView(originalIds, targetAnnotation)) {
            return '';
        }
        // The primary view may have the table settings set and it uses the default path
        return getControlConfigurationPath(pageName) + `/@${UIAnnotationTerms.LineItem}/tableSettings`;
    }
};

export const getQuickVariantPath = (
    pageName: string[],
    manifest: Manifest,
    targetAnnotationEncoded: string,
    columnKey: string,
    targetAnnotation: string,
    configObject: Record<string, unknown>,
    originalIds: string[]
): string => {
    const containerPath = getTableSettingsPathForLR(
        pageName,
        manifest,
        targetAnnotationEncoded,
        columnKey,
        targetAnnotation,
        configObject,
        originalIds
    );
    return containerPath ? containerPath + QUICKVARPATH : '';
};

export const getColumnsPathForView = (
    pageName: string[],
    _manifest: Manifest,
    _targetAnnotationEncoded: string,
    columnKey: string,
    targetAnnotation: string
): string | undefined => {
    const annotationEncoded = targetAnnotation?.replace(/\//gi, '??');
    return annotationEncoded
        ? getControlConfigurationPath(pageName) + `/${annotationEncoded}/columns/${columnKey}`
        : undefined;
};

/**
 * Returns the sync path for the position of a column in a view.
 *
 * @param pageName page name in manifest
 * @param manifest manifest object containing application configuration.
 * @param sectionId section identifier within the view.
 * @param columnKey key identifying the column.
 * @param targetAnnotation annotation term for the target.
 * @returns Sync path string for the column position, or undefined if not found.
 */
export const getColumnPositionPathForView = (
    pageName: string[],
    manifest: Manifest,
    sectionId: string,
    columnKey: string,
    targetAnnotation: string
): string | undefined => {
    const columnsPathForView = getColumnsPathForView(pageName, manifest, sectionId, columnKey, targetAnnotation);
    return columnsPathForView !== undefined ? `${columnsPathForView}/position` : undefined;
};

export const getColumnsPathForLR = (
    pageName: string[],
    manifest: Manifest,
    targetAnnotationEncoded: string,
    columnKey: string,
    targetAnnotation: string
): string | undefined => {
    if (targetAnnotation) {
        return getColumnsPathForView(pageName, manifest, targetAnnotationEncoded, columnKey, targetAnnotation);
    } else {
        return getControlConfigurationPath(pageName) + `/@${UIAnnotationTerms.LineItem}/columns/${columnKey}`;
    }
};

export const getActionsPathForView = (
    pageName: string[],
    _manifest: Manifest,
    _targetAnnotationEncoded: string,
    columnKey: string,
    targetAnnotation: string
): string => {
    const annotationEncoded = targetAnnotation?.replace(/\//gi, '??');
    return annotationEncoded
        ? getControlConfigurationPath(pageName) + `/${annotationEncoded}/actions/${columnKey}`
        : undefined;
};

export const getActionsPathForLR = (
    pageName: string[],
    manifest: Manifest,
    targetAnnotationEncoded: string,
    columnKey: string,
    targetAnnotation: string
): string => {
    if (targetAnnotation) {
        return getActionsPathForView(pageName, manifest, targetAnnotationEncoded, columnKey, targetAnnotation);
    } else {
        return getControlConfigurationPath(pageName) + `/@${UIAnnotationTerms.LineItem}/actions/${columnKey}`;
    }
};

/**
 * Returns the sync path for the position of a action in a view.
 *
 * @param pageName page name in manifest
 * @param manifest manifest object containing application configuration.
 * @param sectionId section identifier within the view.
 * @param actionKey key identifying the action.
 * @param targetAnnotation annotation term for the target.
 * @returns Sync path string for the action position, or undefined if not found.
 */
export const getActionPositionPathForView = (
    pageName: string[],
    manifest: Manifest,
    sectionId: string,
    actionKey: string,
    targetAnnotation: string
): string | undefined => {
    const actionsPathForView = getActionsPathForView(pageName, manifest, sectionId, actionKey, targetAnnotation);
    return actionsPathForView !== undefined ? `${actionsPathForView}/position` : undefined;
};

export const getGroupedActionsPathForLR = (
    pageName: string[],
    manifest: Manifest,
    targetAnnotationEncoded: string,
    columnKey: string,
    targetAnnotation: string,
    configObject: object,
    originalIds: string[]
): string => {
    // views
    if (originalIds && ['overflowGroup', 'priority'].includes(originalIds[originalIds.length - 1])) {
        columnKey = originalIds[originalIds.length - 2];
    }
    if (targetAnnotation) {
        const annotationEncoded = targetAnnotation?.replace(/\//gi, '??');
        return getControlConfigurationPath(pageName) + `/${annotationEncoded}/actions/${columnKey}`;
    }
    // no views
    return getControlConfigurationPath(pageName) + `/@${UIAnnotationTerms.LineItem}/actions/${columnKey}`;
};

export const getGroupedActionsPathForOP = (
    pageName: string[],
    manifest: Manifest,
    sectionId: string,
    columnKey: string,
    sectionIdEncoded: string,
    configObject: object,
    originalIds: string[]
): string => {
    if (originalIds && ['overflowGroup', 'priority'].includes(originalIds[originalIds.length - 1])) {
        columnKey = originalIds[originalIds.length - 2];
    }
    const columnKeyEncoded = columnKey.replace(/\//gi, '??');
    if (sectionId) {
        const sectiondIdEncoded = sectionId.replace(/\//gi, '??');
        return getControlConfigurationPath(pageName) + `/${sectiondIdEncoded}/actions/${columnKeyEncoded}`;
    } else {
        // header action group
        return getSettingsPathForHeaderOP(pageName) + `/actions/${columnKeyEncoded}`;
    }
};

export const getColumnsPathForOP = (
    pageName: string[],
    _manifest: Manifest,
    sectionId: string,
    columnKey: string
): string => {
    return getControlConfigurationPath(pageName) + `/${sectionId}/columns/${columnKey}`;
};

export const getActionsPathForOP = (
    pageName: string[],
    _manifest: Manifest,
    sectionId: string,
    columnKey: string
): string => {
    return getControlConfigurationPath(pageName) + `/${sectionId}/actions/${columnKey.replace('/', '??')}`;
};

export const getHeaderActionsPath = (
    pageName: string[],
    _manifest: Manifest,
    sectionId: string,
    columnKey = ''
): string => {
    return getSettingsPathForHeaderOP(pageName) + `/actions/${columnKey.replace('/', '??')}`;
};

export const getAdditionalSemanticObjectPath = (
    pageName: string[],
    _manifest: Manifest,
    sectionId: string,
    columnKey: string
): string => {
    return getSettingsPathForPage(pageName) + `/additionalSemanticObjects/${columnKey.replace('/', '??')}`;
};

export const getFooterActionsPath = (
    pageName: string[],
    _manifest: Manifest,
    sectionId: string,
    columnKey: string
): string => {
    return getSettingsPathForFooterOP(pageName) + `/actions/${columnKey.replace('/', '??')}`;
};

export const getTableSettingsPathForOP = (pageName: string[], _manifest: Manifest, sectionId: string): string => {
    return getControlConfigurationPath(pageName) + `/${sectionId}/tableSettings`;
};

export const getTableAnalyticalConfigurationPathForLR = (
    pageName: string[],
    manifest: Manifest,
    targetAnnotationEncoded: string,
    columnKey: string,
    targetAnnotation: string,
    configObject: Record<string, unknown>,
    originalIds: string[]
): string => {
    return (
        getTableSettingsPathForLR(
            pageName,
            manifest,
            targetAnnotationEncoded,
            columnKey,
            targetAnnotation,
            configObject,
            originalIds
        ) + `/analyticalConfiguration`
    );
};

/**
 * Common function that calculates the path in manifest for analytical configuration of a list report or ALP table view.
 *
 * @param pageName - page name in manifest
 * @param _manifest - manifest (not used)
 * @param _targetAnnotationEncoded - encoded target annotation (not used)
 * @param _viewKey - view key (not used)
 * @param targetAnnotation - target annotation, i.e. the line item (with or without qualifier)
 * @returns - path as string
 */
export const getTableAnalyticalConfigurationForView = (
    pageName: string[],
    _manifest: Manifest,
    _targetAnnotationEncoded: string,
    _viewKey: string,
    targetAnnotation: string
): string => {
    const tableSettingsPathForView = getTableSettingsPathForView(
        pageName,
        _manifest,
        _targetAnnotationEncoded,
        _viewKey,
        targetAnnotation
    );
    return tableSettingsPathForView !== undefined ? `${tableSettingsPathForView}/analyticalConfiguration` : undefined;
};

export const getTableAnalyticalConfigurationPathForOP = (
    pageName: string[],
    _manifest: Manifest,
    sectionId: string
): string => {
    return getTableSettingsPathForOP(pageName, _manifest, sectionId) + `/analyticalConfiguration`;
};

export const getCustomSectionPath = (
    pageName: string[],
    _manifest: Manifest,
    sectionId: string,
    pathPart?: string
): string => {
    let path = getSettingsPathForPage(pageName) + `/content/body/sections/${sectionId}`;
    if (pathPart) {
        path += '/' + pathPart;
    }
    return path;
};

export const getCustomSectionActionPath = (
    pageName: string[],
    _manifest: Manifest,
    sectionId: string,
    actionId: string
): string => {
    return getSettingsPathForPage(pageName) + `/content/body/sections/${sectionId}/actions/${actionId}`;
};

export const getObjectPageCustomHeaderSectionPath = (
    pageName: string[],
    _manifest: Manifest,
    sectionId: string,
    pathPart?: string
): string => {
    let path = getSettingsPathForPage(pageName) + `/content/header/facets/${sectionId}`;
    if (pathPart) {
        path += '/' + pathPart;
    }
    return path;
};

export const getCustomSubSectionPath = (
    pageName: string[],
    manifest: Manifest,
    sectionId: string,
    pathPart?: string,
    targetAnnotation?: string,
    configObject?: object,
    originalIds?: string[]
): string => {
    let parent = sectionId;

    if (originalIds) {
        if (originalIds[0] === 'sections' && originalIds[1]) {
            parent = originalIds[1];
        }
        if (originalIds[2] === 'subsections' && originalIds[3]) {
            pathPart = `subSections/${originalIds[3]}`;
        }
    }
    let path = getSettingsPathForPage(pageName) + `/content/body/sections/${parent}`;
    if (pathPart) {
        path += '/' + pathPart;
    }
    return path;
};

/**
 * Function that calculates the path in manifest for custom field.
 *
 * @param pageName - page name in manifest
 * @param _manifest - manifest (not used)
 * @param targetAnnotation - field group annotation path
 * @param fieldKey - field key
 * @returns - path as string
 */
export const getCustomFieldPath = (
    pageName: string[],
    _manifest: Manifest,
    targetAnnotation: string,
    fieldKey: string
): string => {
    return getControlConfigurationPath(pageName) + `/${targetAnnotation}/fields/${fieldKey}`;
};

export class ApplicationV4 implements Application {
    $schema?: string;
    id?: string;
    pages?: v4.Pages;
    home?: string;
    target?: Target;
    @childId('settings')
    settings?: AppSettings;
}

export class AppSettings implements v4.AppSettings {
    @syncRule({
        manifest: {
            path: (): string => 'sap.app'
        }
    })
    title?: string;

    @syncRule({
        manifest: {
            path: (): string => 'sap.app'
        }
    })
    description?: string;

    @syncRule({
        manifest: {
            path: (): string => 'sap.fiori'
        }
    })
    cloudDevAdaptationStatus?: CloudDevAdaptationStatus;

    @syncRule({
        manifest: {
            path: (): string => 'sap.ui5/routing/config'
        }
    })
    @validity({
        since: '1.78.0'
    })
    flexibleColumnLayout?: v4.FlexibleColumnLayoutV4;

    @syncRule({
        manifest: {
            path: (): string => 'sap.ui5'
        }
    })
    @validity({
        since: '1.56.0'
    })
    flexEnabled?: boolean;

    @syncRule({
        manifest: {
            path: (): string => 'sap.ui5'
        }
    })
    dependencies?: Dependencies;

    @syncRule({
        manifest: {
            path: (): string => 'sap.fe/app',
            export: (manifestSection, configObject, key): void => {
                if (typeof manifestSection !== 'object') {
                    return;
                }
                const setting = configObject?.['settings']?.[key];
                if (setting && Object.keys(setting).length !== 0) {
                    manifestSection[key] = setting;
                } else if (key in manifestSection) {
                    delete manifestSection[key];
                }
            }
        }
    })
    @validity({
        since: '1.134.0'
    })
    hideDraft?: v4.HideDraft;

    @syncRule({
        manifest: {
            path: (): string => 'sap.fe/macros/table',
            export: (manifestSection, configObject, key): void => {
                if (typeof manifestSection !== 'object') {
                    return;
                }
                const setting = configObject?.['settings']?.[key];
                if (setting && Object.keys(setting).length !== 0) {
                    manifestSection[key] = setting;
                } else if (key in manifestSection) {
                    delete manifestSection[key];
                }
            }
        }
    })
    @validity({
        since: '1.136.5'
    })
    defaultCreationMode?: TableCreationModeType.InlineCreationRows | EmptyOption;

    @syncRule({
        manifest: {
            path: (): string => 'sap.ui5/routing/config'
        }
    })
    viewType?: ViewTypes;

    @syncRule({
        manifest: {
            path: (): string => 'sap.ui5/routing/config'
        }
    })
    path?: string;

    @syncRule(controllerExtensions)
    controllerExtensions?: v4.ControllerExtensions;

    @syncRule({
        manifest: {
            path: (): string => 'sap.fe/macros/table',
            export: (manifestSection, configObject, key): void => {
                if (typeof manifestSection !== 'object') {
                    return;
                }
                const setting = configObject?.['settings']?.[key];
                if (setting && Object.keys(setting).length !== 0) {
                    manifestSection[key] = setting;
                } else if (key in manifestSection) {
                    delete manifestSection[key];
                }
            }
        }
    })
    @validity({
        since: '1.139.0'
    })
    defaultPopinLayout?: PopinLayout;

    @syncRule({
        manifest: {
            path: (): string => 'sap.fe/app'
        }
    })
    @validity({
        since: '1.144.0'
    })
    enableLazyLoading?: boolean;

    @syncRule({
        manifest: {
            path: (): string => 'sap.fe/app'
        }
    })
    @validity({
        since: '1.124.0'
    })
    sideEffectsEventsInteractionType?: v4.SideEffectsEventsInteractionType;
}
